import { type CarConfigDispatchType, useCarConfigSelector } from "../redux/store";
import { showModal } from "../../../../common-deprecated/redux/actions/ModalActions";
import {
    type AphModalIdsType,
    MODAL_EXTRA_PART_OF_PACK,
    MODAL_FINANCE_CALCULATOR,
    MODAL_HIGHLIGHT_FEATURE,
    MODAL_ORDER_CHECKOUT,
    MODAL_OTHER_GRADE,
} from "../utils/modalConstants";
import { type StepsType, trackAvailableInOtherGradesSelectItem } from "../utils/tracking";
import type { GradeFeaturesType } from "../redux/slices/CarConfigGradesSlice";
import { MODAL_ERRORS } from "../../../../common-deprecated/utils/modalConstants";
import type {
    ColourFromOtherGradesType,
    UpholsteryFromOtherGradesType,
    WheelFromOtherGradesType,
} from "../../../shared-logic/types/AvailableInOtherGradesType";
import type { AvailableInOtherGradeModalType } from "../utils/types/SectionTypes";
import { useDispatch } from "../../../../shared-logic/redux/store";
import type { MotorizationType } from "../../../shared-logic/types/Motorization";
import type { ExtraType, PackType } from "../../../../common-deprecated/types/CommonTypes";
import { MODAL_RETAILER_EXTERNAL_QR_CODE } from "../../../../shared-logic/features/retailer/utils/modalConstants";

type showModalHelperSettings = {
    title?: string;
    gradeFeature?: GradeFeaturesType;
    checkoutType?: "buy" | "reserve";
    step?: StepsType;
    origin?: "cc-navigation-top" | "cc-cta";
    availableInOtherGradesProps?: {
        section: AvailableInOtherGradeModalType;
        motorization?: MotorizationType;
        colour?: ColourFromOtherGradesType;
        wheel?: WheelFromOtherGradesType;
        upholstery?: UpholsteryFromOtherGradesType;
    };
    pack?: PackType;
    extra?: ExtraType;
};

type useAphModalType = {
    showModalHelper: (type: AphModalIdsType, settings?: showModalHelperSettings) => void;
};

/**
 * This hook is responsible for using the correct showModal dispatcher.
 *
 * @returns {showModalHelper} showModalHelper function which has to be called in order to show a modal, requires the modals' type.
 */
const useAphModal = (): useAphModalType => {
    const dispatch = useDispatch<CarConfigDispatchType>();
    const { modals } = useCarConfigSelector((state) => state.modal);

    const showModalHelper = (type: AphModalIdsType, settings: showModalHelperSettings = {}): void => {
        const { checkoutType, gradeFeature, step, origin } = settings;

        // Only show modal if the modal isn't already displayed.
        if (modals.length === 0 || !modals.find((modal) => modal.settings?.type === type)?.show) {
            switch (type) {
                case MODAL_HIGHLIGHT_FEATURE: {
                    dispatch(
                        showModal({
                            type,
                            gradeFeature,
                        }),
                    );
                    break;
                }
                case MODAL_OTHER_GRADE: {
                    const availableInOtherGradesProps = settings?.availableInOtherGradesProps;
                    if (availableInOtherGradesProps) {
                        trackAvailableInOtherGradesSelectItem(availableInOtherGradesProps.section);
                        dispatch(
                            showModal({
                                type,
                                availableInOtherGradesProps,
                            }),
                        );
                    }
                    break;
                }
                case MODAL_ORDER_CHECKOUT: {
                    dispatch(
                        showModal({
                            type,
                            checkoutType: checkoutType || "buy",
                        }),
                    );
                    break;
                }
                case MODAL_FINANCE_CALCULATOR: {
                    dispatch(
                        showModal({
                            type,
                            step,
                            origin,
                            injectionLoading: false, // TODO: OR-11115
                        }),
                    );
                    break;
                }
                case MODAL_EXTRA_PART_OF_PACK: {
                    const { pack, extra } = settings;
                    if (pack && extra) {
                        dispatch(
                            showModal({
                                type,
                                pack,
                                extra,
                            }),
                        );
                    }
                    break;
                }
                // Error modal should not be triggered by anything other than the commonPostfetch
                case MODAL_ERRORS: {
                    break;
                }
                // External QR code modal should net be triggered by anything other than useExternalLinkHandler().
                case MODAL_RETAILER_EXTERNAL_QR_CODE: {
                    break;
                }
                default: {
                    dispatch(
                        showModal({
                            type,
                        }),
                    );
                    break;
                }
            }
        }
    };

    return { showModalHelper };
};

export default useAphModal;
